import { PropTypes } from 'prop-types';
import { useEffect, useState } from 'react';
import style from './index.module.scss';
import { useIntl } from 'react-intl';
import AccionaButton from '../../../../../components/AccionaButton';
import dayjs from 'dayjs';
import MixtureData from './components/MixtureData';
import { useDispatch, useSelector } from 'react-redux';
import { SwapRightOutlined } from '@ant-design/icons';
import { MixtureDetailsTable } from '../Create/components/MixtureDetailsModal';
import { getMixture } from '../../../../../services/redux/thermalControl/mixtures/actions';
import ResistanceChart from './components/ResistanceChart';
import AverageTemperatureChart from './components/AverageTemperatureChart';
import ChartWrapper from './components/ChartWrapper';
import { Tabs } from 'antd';

const { wrapper, header, footer, tabs, calibration, parameters } = style;

const View = ({ mixtureId, onCancel }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const mixture = useSelector(({ thermalControlMixtures }) =>
    thermalControlMixtures.list.find((mixture) => mixture.id === mixtureId),
  );
  const isManual = mixture?.device === null;
  const [selectedTab, setSelectedTab] = useState(
    isManual ? 'parameters' : 'averageTemperatureChart',
  );

  useEffect(() => {
    if (isManual) {
      setSelectedTab('parameters');
    }
  }, [isManual]);

  const getMaxDate = (dates) => {
    return dayjs.max(dates);
  };

  const getComponent = () => {
    if (selectedTab === 'parameters') {
      return <MixtureData mixture={mixture} isManual={isManual} />;
    }

    if (selectedTab === 'calibrationChart') {
      return <ResistanceChart mixture={mixture} />;
    }
    if (selectedTab === 'averageTemperatureChart') {
      return (
        <ChartWrapper
          finished={mixture?.finished}
          startTime={mixture?.start_time}
          mixtureName={mixture?.name}
          endTime={
            mixture.parameters.length
              ? getMaxDate(
                  mixture.parameters.map(({ datetime }) => dayjs(datetime)),
                ).toISOString()
              : dayjs().toISOString()
          }
          mixtureId={mixtureId}
          name="temperature"
          render={({ data, loading, range }) => {
            return (
              <AverageTemperatureChart
                data={data}
                loading={loading}
                range={range}
              />
            );
          }}
        />
      );
    }
  };

  useEffect(() => {
    dispatch(getMixture(mixtureId));
  }, [dispatch, mixtureId]);

  return (
    <div className={wrapper}>
      <div className={header}>
        <div>
          <div style={{ display: 'flex' }}>
            <dl>
              <dt>{intl.formatMessage({ id: 'mixture' })}</dt>
              <dd>{mixture?.name}</dd>
            </dl>
            <dl>
              <dt>{intl.formatMessage({ id: 'project' })}</dt>
              <dd>{`${mixture?.project_data?.work_code} ${mixture?.project_data?.name}`}</dd>
            </dl>
          </div>
          <div style={{ display: 'flex' }}>
            <dl>
              <dt>{intl.formatMessage({ id: 'device' })}</dt>
              <dd>
                {isManual
                  ? intl.formatMessage({ id: 'device.manual' })
                  : mixture?.device_data.reference_number}
              </dd>
            </dl>
            <dl>
              <dt>{intl.formatMessage({ id: 'maturity_method' })}</dt>
              <dd>{mixture?.maturity_method}</dd>
            </dl>
            {!isManual && (
              <>
                <dl>
                  <dt>{intl.formatMessage({ id: 'monitoring_time_period' })}</dt>
                  <dd>
                    {dayjs(mixture.start_time).format('DD/MM/YYYY HH:mm')}{' '}
                    <SwapRightOutlined />{' '}
                    {mixture.parameters.length
                      ? getMaxDate(
                          mixture.parameters.map(({ datetime }) =>
                            dayjs(datetime),
                          ),
                        ).format('DD/MM/YYYY HH:mm')
                      : '--'}
                  </dd>
                </dl>
                <dl>
                  <dt>{intl.formatMessage({ id: 'elapsedDays' })}</dt>
                  <dd>
                    {intl.formatMessage(
                      { id: 'totalDays' },
                      {
                        total:
                          dayjs(
                            mixture.parameters.length
                              ? getMaxDate(
                                  mixture.parameters.map(({ datetime }) =>
                                    dayjs(datetime),
                                  ),
                                )
                              : dayjs(),
                          ).diff(dayjs(mixture.start_time), 'days') || 0,
                      },
                    )}
                  </dd>
                </dl>
              </>
            )}
          </div>
        </div>
        <div>
          <dl>
            <dt>{intl.formatMessage({ id: 'composition' })}</dt>
            <dd style={{ maxHeight: '120px', overflowY: 'auto' }}>
              {mixture.details?.length && (
                <MixtureDetailsTable
                  data={mixture.details || []}
                  editable={false}
                  showTotal
                />
              )}
            </dd>
          </dl>
        </div>
      </div>
      <div className={tabs}>
        <Tabs
          activeKey={selectedTab}
          onChange={(tab) => setSelectedTab(tab)}
          items={(isManual
            ? ['parameters']
            : mixture.finished
              ? ['averageTemperatureChart', 'parameters', 'calibrationChart']
              : ['averageTemperatureChart']
          ).map((tab) => ({
            key: tab,
            label: intl
              .formatMessage({
                id: `button${tab[0].toUpperCase()}${tab.substring(1)}`,
              })
              .toUpperCase(),
          }))}
        />
      </div>
      <div className={parameters}>{getComponent()}</div>
      <div className={calibration}>
        <dl>
          <dt>{intl.formatMessage({ id: 'calibration' })}</dt>
          <dd>{mixture?.equations?.equation}</dd>
        </dl>
      </div>
      <div className={`${footer}`}>
        <AccionaButton
          type="primary"
          msg={intl.formatMessage({ id: 'close' })}
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

View.propTypes = {
  mixtureId: PropTypes.number,
  onCancel: PropTypes.func,
};

View.defaultProps = {
  mixtureId: undefined,
  onCancel: () => {},
};
export default View;
